import { api, enviarFormulario } from '../api';

/**
 * A sondagem SPT — ondas 356–357 (spec 12; RN-276). As chamadas e os tipos
 * da `/api/spt`. As contas (N' e N, descrição, sugestão do estado,
 * pendências) vêm prontas do banco (0278).
 */

export type SituacaoSpt = 'rascunho' | 'em_verificacao' | 'devolvida' | 'aprovada' | 'emitida' | 'cancelada';

export interface CampanhaSptDaLista {
  id: string; os_id: string; os_numero: string; cliente: string | null; obra: string | null;
  sequencia: number; numero: string; titulo: string; situacao: SituacaoSpt; criada_em: string;
  furos: number; metragem: number;
}

export interface FuroDaCampanha {
  id: string; codigo: string; profundidade_final: number | null;
  inicio: string | null; termino: string | null; pendencias: number;
  /** 366 · o que a edição rápida mostra e muda. */
  hora_inicio?: string | null; hora_termino?: string | null; sondador?: string | null;
  cota?: number | null; utm_e?: number | null; utm_n?: number | null; utm_fuso?: number | null;
  latitude?: number | null; longitude?: number | null; revestimento_diametro?: number | null;
  na_boletim?: { data: string; hora: string | null; seco: boolean; profundidade: number | null; aberto_ate: number | null } | null;
}

export interface CampanhaSpt {
  id: string; os_id: string; os_numero: string; cliente: string | null; obra: string | null;
  local: string | null; numero: string; sequencia: number; titulo: string; situacao: SituacaoSpt;
  criada_por_nome: string; furos: FuroDaCampanha[]; editavel: boolean;
}

export interface MetroSpt {
  profundidade: number;
  golpes_1: number | null; golpes_2: number | null; golpes_3: number | null;
  penet_1: number | null; penet_2: number | null; penet_3: number | null;
  amostra: string | null;
  n_ini_texto: string; n_ini_valor: number | null; n_texto: string; n_valor: number | null;
  /** 362 · NBR 6484 5.2.3.16: o amostrador não recuperou amostra. */
  sem_recuperacao?: boolean;
}

export interface CamadaSpt {
  id: string; topo: number; base: number; material: string | null; complemento: number | null;
  estado: string | null; plasticidade: number | null; cores: number[]; genese: string | null;
  genero: 'F' | 'M' | null; classe: number | null; sugestao: string | null; descricao: string | null;
  tinta: string | null;
}

export interface AvancoSpt { metodo: 'TC' | 'TH' | 'CA' | 'REV'; de: number; ate: number }
export interface LeituraNa {
  ordem?: number; data: string; hora: string | null; seco: boolean; profundidade: number | null; no_boletim: boolean;
  /** 362 · 5.2.5.5: até onde o furo permanece aberto. */
  aberto_ate?: number | null;
}
export interface FenomenoSpt { id?: string; tipo: string; de: number; ate: number | null; dado: number | null; texto?: string }
export interface Pendencia { codigo: string; texto: string }

export interface FuroSpt {
  id: string; campanha_id: string; codigo: string; inicio: string | null; termino: string | null;
  sondador: string | null; sistema: 'manual' | 'mecanizado'; cota: number | null;
  utm_e: number | null; utm_n: number | null; utm_fuso: number | null;
  latitude: number | null; longitude: number | null;
  profundidade_final: number | null; criterio_paralisacao: string | null;
  lavagem_1: number | null; lavagem_2: number | null; lavagem_3: number | null;
  /** 362 · 7.2.3 b e 7.1 e. */
  revestimento_diametro?: number | null; hora_inicio?: string | null; hora_termino?: string | null;
  campanha_situacao: SituacaoSpt; sequencia: number;
  metros: MetroSpt[]; camadas: CamadaSpt[]; avanco: AvancoSpt[]; na: LeituraNa[];
  fenomenos: FenomenoSpt[]; recomendacoes: string[]; pendencias: Pendencia[]; editavel: boolean;
}

export interface Vocabulario {
  materiais: { codigo: string; genero: 'F' | 'M'; classe: number; tipo: 'S' | 'O' | 'R' }[];
  complementos: { id: number; termo: string }[];
  plasticidades: { id: number; termo: string }[];
  cores: { id: number; termo: string; tinta: string }[];
  geneses: { sigla: string; nome: string; fora_da_norma: boolean }[];
  estados: { classe: number; ordem: number; ate: number | null; termo: string }[];
  criterios: { codigo: string; nome: string; texto_padrao: string; conferido: boolean }[];
  fenomenos: { codigo: string; nome: string; faixa: boolean; dado_proprio: string | null; texto_padrao: string }[];
  recomendacoes: { codigo: string; texto: string; conferido: boolean }[];
}

export const NOME_DA_SITUACAO_SPT: Record<SituacaoSpt, string> = {
  rascunho: 'Rascunho', em_verificacao: 'Em verificação', devolvida: 'Devolvida',
  aprovada: 'Aprovada', emitida: 'Emitida', cancelada: 'Cancelada',
};

/** «[A/O]» pelo gênero (S04); a cor é sempre feminina. */
export const comGenero = (termo: string, genero: 'F' | 'M' | null | undefined) =>
  termo.replace('[A/O]', genero === 'M' ? 'O' : 'A');

/** Metro no padrão brasileiro: 30,45. */
export const metro = (n: number | null | undefined, casas = 2) =>
  n == null ? '' : n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });

/** Lê «30,45» ou «30.45»; vazio é null; o que não é número é NaN. */
export const lerNumero = (t: string): number | null => {
  const s = t.trim().replace(/\./g, (m, _i, all) => (all.includes(',') ? '' : m)).replace(',', '.');
  if (!s) return null;
  const v = Number(s);
  return Number.isFinite(v) ? v : Number.NaN;
};

export const vocabularioSpt = () => api<Vocabulario>('/spt/vocabulario');
export const listarCampanhasSpt = (q: { os?: string; busca?: string } = {}) => {
  const p = new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]);
  return api<CampanhaSptDaLista[]>(`/spt/campanhas${p.size ? `?${p}` : ''}`);
};
export const criarCampanhaSpt = (osId: string, titulo: string) =>
  api<CampanhaSpt>('/spt/campanhas', { metodo: 'POST', corpo: { osId, titulo } });
export const verCampanhaSpt = (id: string) => api<CampanhaSpt>(`/spt/campanhas/${id}`);
/** 366 · a edição rápida: vários furos de uma vez, o motivo uma vez, todos ou nenhum. */
export const alterarFurosSpt = (campanhaId: string, furos: Record<string, unknown>[], motivo: string) =>
  api<CampanhaSpt>(`/spt/campanhas/${campanhaId}/furos`, { metodo: 'PATCH', corpo: { furos, motivo } });
export const criarFuroSpt = (campanhaId: string, codigo: string) =>
  api<FuroSpt>(`/spt/campanhas/${campanhaId}/furos`, { metodo: 'POST', corpo: { codigo } });
export const cancelarFuroSpt = (id: string, motivo: string) =>
  api<CampanhaSpt>(`/spt/furos/${id}/cancelar`, { metodo: 'POST', corpo: { motivo } });
export const verFuroSpt = (id: string) => api<FuroSpt>(`/spt/furos/${id}`);
export const alterarFuroSpt = (id: string, corpo: Record<string, unknown>) =>
  api<FuroSpt>(`/spt/furos/${id}`, { metodo: 'PATCH', corpo });
export const gravarMetroSpt = (id: string, prof: number,
  corpo: { golpes: (number | null)[]; penet: (number | null)[]; amostra?: string | null; semRecuperacao?: boolean }) =>
  api<FuroSpt>(`/spt/furos/${id}/metros/${prof}`, { metodo: 'PUT', corpo });
export const apagarMetroSpt = (id: string, prof: number) =>
  api<FuroSpt>(`/spt/furos/${id}/metros/${prof}`, { metodo: 'DELETE' });
export const gravarAvancoSpt = (id: string, corpo: AvancoSpt[]) =>
  api<FuroSpt>(`/spt/furos/${id}/avanco`, { metodo: 'PUT', corpo });
export const gravarNaSpt = (id: string, corpo: { data: string; hora?: string | null; seco: boolean;
  profundidade?: number | null; noBoletim?: boolean }[]) =>
  api<FuroSpt>(`/spt/furos/${id}/na`, { metodo: 'PUT', corpo });
export const gravarFenomenosSpt = (id: string, corpo: { tipo: string; de: number; ate?: number | null; dado?: number | null }[]) =>
  api<FuroSpt>(`/spt/furos/${id}/fenomenos`, { metodo: 'PUT', corpo });
export const gravarRecomendacoesSpt = (id: string, corpo: string[]) =>
  api<FuroSpt>(`/spt/furos/${id}/recomendacoes`, { metodo: 'PUT', corpo });
export const dividirCamadaSpt = (furoId: string, prof: number) =>
  api<FuroSpt>(`/spt/furos/${furoId}/camadas/dividir`, { metodo: 'POST', corpo: { prof } });
export const alterarCamadaSpt = (camadaId: string, corpo: Record<string, unknown>) =>
  api<FuroSpt>(`/spt/camadas/${camadaId}`, { metodo: 'PATCH', corpo });
export const excluirCamadaSpt = (camadaId: string, juntarCom: 'cima' | 'baixo') =>
  api<FuroSpt>(`/spt/camadas/${camadaId}/excluir`, { metodo: 'POST', corpo: { juntarCom } });
export const unirCamadaSpt = (camadaId: string) =>
  api<FuroSpt>(`/spt/camadas/${camadaId}/unir`, { metodo: 'POST' });

// ── O relatório (ondas 358/359) ─────────────────────────────────────

export interface AssinanteSpt { nome: string; cargo: string | null; em: string }
export interface RelatorioSpt {
  situacao: SituacaoSpt; motivo: string | null; numero: string | null;
  relatorio: { id: string; status: string; elaborado: AssinanteSpt | null; verificado: AssinanteSpt | null; aprovado: AssinanteSpt | null } | null;
  falta: Pendencia[];
  emissoes: { id: string; numero: string; revisao: number; status: string; data_emissao: string }[];
  textos: Record<string, string>; padrao: Record<string, string>; secoes: { chave: string; titulo: string }[];
  locacao: 'croqui' | 'planta';
  croqui: { origem: 'mapa' | 'imagem' } | null; temCroqui: boolean;
  anexos: { arquivoId: string; titulo: string; origem: string | null }[];
  furosComCoordenada: { codigo: string; latitude: number; longitude: number }[];
  editavel: boolean;
}
export const verRelatorioSpt = (id: string) => api<RelatorioSpt>(`/spt/campanhas/${id}/relatorio`);
export const gravarRelatorioSpt = (id: string, corpo: { textos?: Record<string, string>; locacao?: 'croqui' | 'planta' }) =>
  api<RelatorioSpt>(`/spt/campanhas/${id}/relatorio`, { metodo: 'PATCH', corpo });
export const acaoDoRelatorioSpt = (id: string, acao: 'enviar' | 'verificar' | 'devolver' | 'emitir' | 'revisao', corpo?: unknown) =>
  api<RelatorioSpt>(`/spt/campanhas/${id}/${acao}`, { metodo: 'POST', corpo });
export const enviarCroquiSpt = (id: string, f: FormData, aoProgredir?: (x: number) => void) =>
  enviarFormulario<RelatorioSpt>(`/spt/campanhas/${id}/croqui`, f, aoProgredir);
export const tirarCroquiSpt = (id: string) => api<RelatorioSpt>(`/spt/campanhas/${id}/croqui`, { metodo: 'DELETE' });
export const anexarPlantaSpt = (id: string, f: FormData, aoProgredir?: (x: number) => void) =>
  enviarFormulario<RelatorioSpt>(`/spt/campanhas/${id}/anexos`, f, aoProgredir);
export const tirarPlantaSpt = (id: string, k: number) => api<RelatorioSpt>(`/spt/campanhas/${id}/anexos/${k}`, { metodo: 'DELETE' });

/** 362 · a referência de nível (RN) da OS — NBR 6484 5.2.1 e 7.2.2 b; vale para todos os ensaios de campo. */
export interface ReferenciaNivel {
  descricao: string; cota: number; tipo: 'georreferenciada' | 'arbitraria'; latitude: number | null; longitude: number | null;
}
export const verRnDaOs = (osId: string) => api<{ rn: ReferenciaNivel | null }>(`/campo/os/${osId}/rn`);
export const gravarRnDaOs = (osId: string, corpo: Partial<ReferenciaNivel>) =>
  api<{ rn: ReferenciaNivel }>(`/campo/os/${osId}/rn`, { metodo: 'PUT', corpo });
/** 366 · a mesma RN, lançada de dentro da campanha SPT (com a permissão do SPT). */
export const verRnDaCampanhaSpt = (id: string) => api<{ rn: ReferenciaNivel | null }>(`/spt/campanhas/${id}/rn`);
export const gravarRnDaCampanhaSpt = (id: string, corpo: Partial<ReferenciaNivel>) =>
  api<{ rn: ReferenciaNivel }>(`/spt/campanhas/${id}/rn`, { metodo: 'PUT', corpo });
